import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Header } from '../../components/layout/Header'
import { Card } from '../../components/common/Card'
import axios from 'axios'
import { useTheme } from '../../contexts/ThemeContext'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://192.168.0.98:8090/api'

interface CategoryData {
  category: string
  product_count: number
  total_value: number
  avg_price: number
  top_markets: string[]
}

export const CategoriesPage: React.FC = () => {
  const { colors } = useTheme()

  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics-categories'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_BASE_URL}/markets/analytics/categories`)
      return data
    },
  })

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div className="loading-spinner" style={{
              width: '40px',
              height: '40px',
              border: '4px solid #E0E0E0',
              borderTop: '4px solid #4A90E2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: colors.textSecondary }}>Loading category analytics...</p>
          </div>
        </main>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <Card style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ color: '#EF4444' }}>Error loading data: {(error as Error).message}</p>
          </Card>
        </main>
      </div>
    )
  }

  const categories: CategoryData[] = data?.categories || []

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
            Product Categories Analysis
          </h1>
          <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
            Analyze product performance across different categories from the Expandly database
          </p>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', marginBottom: '32px' }}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Total Categories</p>
              <p style={{ fontSize: '32px', fontWeight: 700, color: colors.primary }}>{categories.length}</p>
            </div>
          </Card>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Total Products</p>
              <p style={{ fontSize: '32px', fontWeight: 700, color: '#50C878' }}>
                {categories.reduce((sum, c) => sum + c.product_count, 0).toLocaleString()}
              </p>
            </div>
          </Card>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Total Value</p>
              <p style={{ fontSize: '32px', fontWeight: 700, color: '#7B68EE' }}>
                ${categories.reduce((sum, c) => sum + c.total_value, 0).toLocaleString()}
              </p>
            </div>
          </Card>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: colors.textSecondary, fontSize: '14px', marginBottom: '8px' }}>Avg Price</p>
              <p style={{ fontSize: '32px', fontWeight: 700, color: '#FF6B35' }}>
                ${(categories.reduce((sum, c) => sum + c.avg_price, 0) / categories.length || 0).toFixed(2)}
              </p>
            </div>
          </Card>
        </div>

        {/* Categories Table */}
        <Card>
          <h2 style={{ fontSize: '20px', marginBottom: '16px', color: colors.textPrimary }}>Categories Breakdown</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                  <th style={{ textAlign: 'left', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Category</th>
                  <th style={{ textAlign: 'right', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Products</th>
                  <th style={{ textAlign: 'right', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Total Value</th>
                  <th style={{ textAlign: 'right', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Avg Price</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Top Markets</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category, idx) => (
                  <tr key={category.category} style={{
                    borderBottom: `1px solid ${colors.border}`,
                    backgroundColor: idx % 2 === 0 ? 'transparent' : colors.background,
                  }}>
                    <td style={{ padding: '14px 12px', fontWeight: 500, color: colors.textPrimary }}>{category.category}</td>
                    <td style={{ padding: '14px 12px', textAlign: 'right', color: colors.textPrimary }}>{category.product_count.toLocaleString()}</td>
                    <td style={{ padding: '14px 12px', textAlign: 'right', color: colors.textPrimary }}>${category.total_value.toLocaleString()}</td>
                    <td style={{ padding: '14px 12px', textAlign: 'right', color: colors.textPrimary }}>${category.avg_price.toFixed(2)}</td>
                    <td style={{ padding: '14px 12px' }}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {(category.top_markets || []).slice(0, 3).map(market => (
                          <span key={market} style={{
                            padding: '4px 8px',
                            backgroundColor: colors.primary + '20',
                            color: colors.primary,
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 500,
                          }}>{market}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </main>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
